/**
 * Route Royal Property Card Component
 * 
 * Simple property card for Route Royal marketing theme.
 * Now uses FormatPrice for currency-aware price display.
 * Includes WishlistButton for saving properties.
 */

'use client';

import Image from 'next/image';
import type { PropertyCardProps } from '@routeroyal/theme-core';
import { FormatPrice } from '@/components/ui/FormatPrice';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import { WishlistButton } from '@/components/customer/WishlistButton';

/**
 * Extract price value and currency from a price string
 * e.g., "$500,000" -> { amount: 500000, currency: "USD" }
 * e.g., "€450,000" -> { amount: 450000, currency: "EUR" }
 * e.g., "£350,000" -> { amount: 350000, currency: "GBP" }
 */
function parsePriceString(priceStr: string | undefined): { amount: number | null; currency: string } {
  if (!priceStr) {
    return { amount: null, currency: DEFAULT_CURRENCY };
  }

  // Detect currency from symbol
  let currency = DEFAULT_CURRENCY;
  if (priceStr.includes('€')) currency = 'EUR';
  else if (priceStr.includes('£')) currency = 'GBP';
  else if (priceStr.includes('R') && !priceStr.includes('Rs')) currency = 'ZAR';
  else if (priceStr.includes('Rs')) currency = 'MUR';
  else if (priceStr.includes('A$')) currency = 'AUD';

  // Extract numeric value
  const numericMatch = priceStr.match(/[\d,.]+/);
  if (!numericMatch) {
    return { amount: null, currency };
  }

  const amount = parseFloat(numericMatch[0].replace(/,/g, ''));
  return { amount: isNaN(amount) ? null : amount, currency };
}

interface ExtendedPropertyCardProps extends PropertyCardProps {
  /** Whether to show the wishlist (heart) button */
  showWishlistButton?: boolean;
}

export function RouteRoyalPropertyCard({
  property,
  showWishlistButton = true,
}: ExtendedPropertyCardProps) {
  // Parse the price string to get amount and currency
  const { amount, currency } = parsePriceString(property.price);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
      {property.image && (
        <div className="relative h-48 w-full bg-gray-200">
          <Image
            src={property.image}
            alt={property.name}
            fill
            className="object-cover"
          />
          {/* Wishlist Button */}
          {showWishlistButton && (
            <div className="absolute top-2 left-2 z-10">
              <WishlistButton
                entityType="PROPERTY"
                entityId={property.id}
                variant="solid"
                size="sm"
              />
            </div>
          )}
        </div>
      )}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{property.name}</h3>
        <p className="text-gray-600 text-sm mb-2">{property.address}</p>
        
        {/* Price - Now uses FormatPrice for currency conversion */}
        <div className="text-xl font-bold text-gray-900 mb-2">
          <FormatPrice
            amount={amount}
            currency={currency}
            size="lg"
          />
        </div>
        
        {(property.bedrooms || property.bathrooms || property.area) && (
          <div className="flex items-center gap-4 text-sm text-gray-600">
            {property.bedrooms && <span>{property.bedrooms} beds</span>}
            {property.bathrooms && <span>{property.bathrooms} baths</span>}
            {property.area && <span>{property.area}</span>}
          </div>
        )}
        {property.status && (
          <span className={`inline-block mt-2 px-2 py-1 text-xs rounded capitalize ${
            property.status === 'available' ? 'bg-green-100 text-green-800' :
            (property.status === 'sold' || property.status === 'reserved') ? 'bg-gray-100 text-gray-800' :
            'bg-yellow-100 text-yellow-800'
          }`}>
            {property.status === 'reserved' ? 'sold' : property.status}
          </span>
        )}
      </div>
    </div>
  );
}
